import type StoreService from '@ember-data/store';
import type LocationModel from 'garaje/models/location';
import type LocationsCapacity from 'garaje/models/locations-capacity';
import moment from 'moment-timezone';

import type { RecordArray } from './type-utils';
const DATE_FORMAT = 'YYYY-MM-DD';
export const LOCATION_AT_CAPACITY_MESSAGE = 'Location at capacity. Please select another date.';

interface LocationCapacityParams {
  filter: {
    location: string;
    'capacity-date'?: string;
    'start-date'?: string;
    'end-date'?: string;
  };
}

/**
 * Generates parameters used for querying locations-capacity models
 *
 * @param  locationId Location ID
 * @param  start Query start date
 * @param  end Query end date
 * @returns tuple of params
 */
export function getParamsForQueryLocationsCapacity(
  locationId: string,
  start: Date | string,
  end?: Date | string,
): ['locations-capacity', LocationCapacityParams] {
  const startDate = moment(start || undefined).format(DATE_FORMAT);
  const endDate = moment(end || start || undefined).format(DATE_FORMAT);
  const params: LocationCapacityParams = {
    filter: {
      location: locationId,
    },
  };

  if (startDate === endDate) {
    params.filter['capacity-date'] = startDate;
  } else {
    params.filter['start-date'] = startDate;
    params.filter['end-date'] = endDate;
  }

  return ['locations-capacity', params];
}

/**
 * Fetches a single locations-capacity model by date
 *
 * @param store - StoreService
 * @param location - LocationModel
 * @param startDate - Query start date
 * @param endDate - Query end date
 * @returns Promise for LocationsCapacityModel
 */
export function fetchCapacity(
  store: StoreService,
  location: LocationModel,
  startDate: Date | string,
  endDate?: Date | string,
): Promise<LocationsCapacity | undefined> {
  return store
    .query(...getParamsForQueryLocationsCapacity(location.id, startDate, endDate))
    .then((capacities) => capacities.slice()[0]);
}

/**
 * Fetches locations-capacity models by date range
 *
 * @param store StoreService
 * @param location LocationModel
 * @param startDate Query start date
 * @param endDate Query end date
 * @returns Promise for array of LocationsCapacityModel
 */
export function fetchCapacities(
  store: StoreService,
  location: LocationModel,
  startDate: Date | string,
  endDate: Date | string,
): Promise<RecordArray<LocationsCapacity>> {
  return store.query(...getParamsForQueryLocationsCapacity(location.id, startDate, endDate));
}

/**
 * Generates an ember-changeset validator function for validating the capacity limit for a given day
 *
 * @param  {object} store StoreService
 * @param  {object} location LocationModel
 * @returns {(key: string, value: unknown) => Promise<boolean>} ember-changeset validator function
 */
export function createCapacityValidator(store: StoreService, location: LocationModel) {
  if (!location.capacityLimitEnabled) {
    return () => true;
  }
  return function validateCapacity(
    key: string,
    newValue: string | Date,
    _oldValue: unknown,
    changes: Record<string, unknown>,
    _content: unknown,
  ): boolean | Promise<boolean | string> {
    // Do not run validation on a value that did not change
    if (!changes[key]) return true;

    return fetchCapacity(store, location, newValue).then((locationsCapacity) => {
      if (locationsCapacity) {
        return locationsCapacity.hasCapacity || LOCATION_AT_CAPACITY_MESSAGE;
      }
      return true;
    });
  };
}
